import type { RequestKind, TaskCommand, TaskCommandType, TaskListItemDto, TaskStatus } from '@ovo/shared'
import { STATUS_LABELS } from '@ovo/shared/domain'

export { PRIORITY_LABELS, STATUS_LABELS } from '@ovo/shared/domain'

const OPEN: readonly TaskStatus[] = ['new', 'accepted', 'in_progress', 'blocked', 'submitted']

export function isOpen(status: TaskStatus): boolean {
  return OPEN.includes(status)
}

export function statusLabel(status: TaskStatus): string {
  return STATUS_LABELS[status]
}

export const REQUEST_LABELS: Record<RequestKind, string> = {
  question: 'Вопрос',
  due_change: 'Перенос срока',
}

/** Подписи действий (глаголы). */
export const ACTION_LABELS: Record<TaskCommandType, string> = {
  accept: 'Принять',
  decline: 'Отклонить…',
  start: 'Начать',
  submit: 'Готово',
  verify: 'Принять работу',
  return: 'Вернуть на доработку',
  block: 'Есть блокер…',
  unblock: 'Блокер снят',
  on_track: 'Всё по плану',
  snooze: 'Напомнить позже…',
  request_due: 'Перенести срок…',
  decide_due: 'Решить по сроку',
  ask: 'Есть вопрос…',
  answer: 'Ответить на вопрос…',
  set_due: 'Изменить срок…',
  edit: 'Изменить задачу',
  reassign: 'Передать другому…',
  cancel: 'Отменить задачу…',
  reopen: 'Вернуть в работу',
  comment: 'Комментировать',
  set_co_assignees: 'Исполнители…',
  checklist_add: 'Добавить пункт',
  checklist_toggle: 'Отметить пункт',
  checklist_remove: 'Удалить пункт',
}

export const ACTION_SUCCESS: Partial<Record<TaskCommandType, string>> = {
  accept: 'Задача принята',
  decline: 'Задача отклонена — постановщик узнает причину',
  start: 'Отлично, задача в работе',
  submit: 'Готово! Постановщик получит уведомление',
  verify: 'Работа принята',
  return: 'Вернули на доработку',
  block: 'Отметили блокер — постановщик в курсе',
  unblock: 'Блокер снят, задача снова в работе',
  on_track: 'Отметили: всё по плану',
  snooze: 'Напомним позже',
  request_due: 'Запрос на перенос срока отправлен',
  decide_due: 'Решение по сроку отправлено',
  ask: 'Вопрос отправлен',
  answer: 'Ответ отправлен',
  set_due: 'Срок обновлён',
  edit: 'Изменения сохранены',
  reassign: 'Задача передана',
  cancel: 'Задача отменена',
  reopen: 'Задача снова в работе',
  comment: 'Комментарий отправлен',
}

/** Действия, которым нужен ввод (причина, дата, человек) — открывают шторку. */
export const INPUT_ACTIONS: ReadonlySet<TaskCommandType> = new Set<TaskCommandType>([
  'decline',
  'block',
  'return',
  'ask',
  'answer',
  'request_due',
  'set_due',
  'reassign',
  'cancel',
  'snooze',
  'decide_due',
])

/**
 * Клиентская оценка доступных действий для элемента списка (у списка нет allowedActions).
 * Окончательно права проверяет сервер.
 */
export function listActions(task: TaskListItemDto, meId: number): TaskCommandType[] {
  const isAssignee = task.assignee.id === meId
  const isCreator = task.creator.id === meId
  const out: TaskCommandType[] = []
  if (isAssignee) {
    switch (task.status) {
      case 'new':
        out.push('accept', 'decline', 'ask')
        break
      case 'accepted':
        out.push('start', 'submit', 'block', 'request_due', 'ask')
        break
      case 'in_progress':
        out.push('submit', 'on_track', 'block', 'request_due', 'ask')
        break
      case 'blocked':
        out.push('unblock', 'request_due', 'ask')
        break
      default:
        break
    }
  }
  if (isCreator) {
    if (task.status === 'submitted') out.push('verify', 'return')
    if (isOpen(task.status)) out.push('set_due', 'reassign', 'cancel')
    else out.push('reopen')
  }
  return [...new Set(out)]
}

/** Предсказание нового статуса для оптимистичного обновления. null — статус не меняется. */
export function predictStatus(
  task: Pick<TaskListItemDto, 'status' | 'requiresAcceptance' | 'creator' | 'assignee'> & {
    prevStatus?: TaskStatus | null
  },
  command: TaskCommand,
): TaskStatus | null {
  const self = task.creator.id === task.assignee.id
  switch (command.type) {
    case 'accept':
      return 'accepted'
    case 'decline':
      return 'declined'
    case 'start':
      return 'in_progress'
    case 'submit':
      return self || !task.requiresAcceptance ? 'done' : 'submitted'
    case 'verify':
      return 'done'
    case 'return':
      return 'in_progress'
    case 'block':
      return 'blocked'
    case 'unblock':
      return task.prevStatus && task.prevStatus !== 'blocked' ? task.prevStatus : 'in_progress'
    case 'cancel':
      return 'cancelled'
    case 'reopen':
      return 'in_progress'
    case 'reassign':
      return 'new'
    default:
      return null
  }
}

/** Короткое имя: «Иван Петров» → «Иван П.» */
export function shortName(displayName: string): string {
  const parts = displayName.trim().split(/\s+/)
  if (parts.length < 2) return displayName
  const [first, last] = parts as [string, string]
  return `${first} ${last.charAt(0)}.`
}

export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  const a = parts[0]?.charAt(0) ?? '?'
  const b = parts[1]?.charAt(0) ?? ''
  return (a + b).toUpperCase()
}
